import { Button, Container, Flex, FormControl, Input, ScaleFade, Text, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Warning } from 'components/Warning';
import { UserPermissions } from 'configs/routes';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import useUpdateVinMutation from 'hooks/mutations/useUpdateVinMutation';
import { upperCase } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { ButtonSpinner, LoadingSpinner } from '../../components/LoadingSpinner';
import localconfig from '../../config';
import { fontWeights } from '../../global/Fonts';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useServiceRecordQuery from '../../hooks/useServiceRecordQuery';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';

export interface IVehicleIdentificationForm {
  vin: string;
}

const VinUpdatePage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const axios = useAxios();
  const { user } = useUserContext();
  const { id } = useParams();

  const serviceRecordId = Number(id);
  const canChangeVin = user?.permissions?.includes(UserPermissions.SERVICEENTRYCHANGEVIN);

  useEffect(() => {
    if (user != null && !canChangeVin) {
      navigate(`/app/op/service-record/${serviceRecordId}`, { replace: true });
    }
  }, [user, canChangeVin, navigate, serviceRecordId]);

  const schema = yup
    .object({
      vin: yup
        .string()
        .trim()
        .matches(/([0-9ABCDEFGHJKLMNPRSTUVWXYZ]{16,17})\w+/g, t('forms:vin.format'))
        .required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    register,
    setValue,
    setError,
  } = useForm<IVehicleIdentificationForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const [actualVin, setActualVin] = useState('');
  const [vinCouldBeInvalid, setVinCouldBeInvalid] = useState(false);
  const serviceRecordQuery = useServiceRecordQuery(serviceRecordId);

  const updateVinMutation = useUpdateVinMutation(serviceRecordQuery.data?.service_activity?.id, useServiceRecordQuery.getKey(serviceRecordId));

  const sendRequest = async ({
    // @ts-ignore
    url,
    method = 'put',
    dataForEndpoint = {},
    params = {},
    configData = {},
  }) => {
    const axiosCall =
      method !== 'get'
        ? // @ts-ignore
          axios[method](`${localconfig.apiBaseUrl}/op/v1/${url}`, dataForEndpoint, { params })
        : // @ts-ignore
          axios[method](`${localconfig.apiBaseUrl}/op/v1/${url}`, configData);
    return await axiosCall.catch((err: { response: { status: number } }) => {
      manageErrorResponse(toast, err.response);
      return Promise.reject(err);
    });
  };

  const { mutate, isPending } = useMutation<any, Error, IVehicleIdentificationForm>({
    mutationFn: async (data) =>
      sendRequest({
        method: 'get',
        url: `vehicle/${data.vin}/validate`,
      }),
  });

  const onSubmit: SubmitHandler<IVehicleIdentificationForm> = (data) => {
    mutate(data, {
      onSuccess: (validCheckData) => {
        const {
          data: { check_digit_valid },
        } = validCheckData;

        setActualVin(data.vin);
        if (!check_digit_valid && vinCouldBeInvalid) {
          setVinCouldBeInvalid(true);
          return;
        }
        setVinCouldBeInvalid(false);

        updateVinMutation.mutate(
          { vin: data?.vin },
          {
            onSuccess: () => {
              navigate(`/app/op/service-record/${id}`);
            },
            onError: (err) => {
              const { errors, message } = (err as unknown as { response: { status: number; data: any } }).response.data;

              setError('vin', {
                type: 'server',
                message: message ?? errors['vin']!.join('. '),
              });
            },
          },
        );
      },
      onError: (error: any) => {
        const { errors, message } = error.response.data;

        setError('vin', {
          type: 'server',
          message: message ?? errors['vin']!.join('. '),
        });
      },
    });
  };

  const { onChange: onChangeVin } = register('vin');

  const changeVinAndValidate = useCallback(
    (event: ChangeEvent) => {
      vinCouldBeInvalid && setVinCouldBeInvalid(false);
      onChangeVin(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vinCouldBeInvalid],
  );

  if (user == null || !canChangeVin) {
    return null;
  }

  if (isNaN(serviceRecordId)) {
    navigate('/404', { replace: true });
    return null;
  }

  if (serviceRecordQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentLayout>
      <ContentCard isLoading={serviceRecordQuery.isFetching} header={t('service_record:vin.update.modal.title')}>
        <ScaleFade initialScale={0.9} in={vinCouldBeInvalid} unmountOnExit>
          <Warning>
            <Text fontSize="sm" color="muted" data-test-id="possible-invalid-vin-caption">
              {t('forms:vin.validation.possibly_invalid')}
            </Text>
            <Button
              type="button"
              variant="outline"
              disabled={!isValid || serviceRecordQuery.isFetching}
              data-test-id="possible-invalid-vin-proceed-button"
              onClick={() => {
                sendRequest({
                  url: `service-activity/${serviceRecordQuery.data?.service_activity?.id}/update-vin`,
                  dataForEndpoint: { vin: actualVin },
                }).catch((err: { response: { status: number } }) => {
                  //@ts-ignore
                  const { errors, message } = err.response.data;

                  setError('vin', {
                    type: 'server',
                    message: message ?? errors['vin']!.join('. '),
                  });
                });
                navigate(`/app/op/service-record/${serviceRecordQuery.data?.id}`);
              }}
            >
              {!isPending ? t('common:proceed') : <ButtonSpinner />}
            </Button>
          </Warning>
        </ScaleFade>
        <Container as="form" p={4} textAlign={'center'} onSubmit={handleSubmit(onSubmit)}>
          <Text px={4} fontWeight={fontWeights.bold} fontSize={'lg'} as={'div'} textAlign={'left'}>
            {`${t('service_record:vin.update.modal.original')}  `}
            <Text as={'span'} color="red">
              {serviceRecordQuery.data?.vehicle?.vin}
            </Text>
          </Text>
          <FormControl id="vin" isInvalid={!!errors.vin?.message} p={4}>
            <Input
              type="string"
              textAlign={'center'}
              placeholder={t('forms:vin.placeholder')}
              {...register('vin')}
              onChange={changeVinAndValidate}
              onInput={(e) =>
                //@ts-ignore
                setValue('vin', upperCase(e.target.value).replace(/ /g, ''))
              }
            />
            <Text fontSize="sm" color={'red.500'}>
              {errors.vin?.message}
            </Text>
          </FormControl>

          <Flex flexDirection={'row'} justifyContent={'flex-end'} pr={4}>
            <Button
              type="submit"
              variant="primary"
              alignSelf={'flex-end'}
              disabled={(submitCount! > 0 && !isValid) || isPending || vinCouldBeInvalid}
              data-test-id="identification-manual-submit-button"
            >
              {!isPending ? t('common:save') : <ButtonSpinner />}
            </Button>
          </Flex>
        </Container>
      </ContentCard>
    </ContentLayout>
  );
};

// @ts-ignore
export default VinUpdatePage;
